import React, { useEffect, useRef, useState } from "react";
import { Share2, Link2, Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
export const shareKindFor = (t) => (t.is_pista && !t.track_id ? { kind: "pista", id: t.id } : (!t.category && t.pack_id && !t.is_pista) ? { kind: "pack", id: t.pack_id } : { kind: "track", id: t.track_id || t.id });
export const shareUrlFor = (t) => { const k = shareKindFor(t); return `${API}/share/${k.kind}/${k.id}`; };
export const storyUrlFor = (t) => { const k = shareKindFor(t); return `${API}/story-image/${k.kind}/${k.id}`; };
export const sharePackUrl = (id) => `${API}/share/pack/${id}`;
export const shareTrackUrl = (id) => `${API}/share/track/${id}`;

// Replaces the address-bar URL with the shareable /api/share/... link so copy-paste previews show the cover.
export function useShareableUrl(kind, id) {
  const loc = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (id && !loc.pathname.startsWith("/api/share/")) navigate(`/api/share/${kind}/${id}${loc.search}`, { replace: true });
    // eslint-disable-next-line
  }, [kind, id]);
}
export const storyPackUrl = (id) => `${API}/story-image/pack/${id}`;
export const storyTrackUrl = (id) => `${API}/story-image/track/${id}`;

export async function shareLink(url, title) {
  try {
    if (navigator.share) { await navigator.share({ title, url }); return; }
  } catch (e) { if (e?.name === "AbortError") return; }
  try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar el enlace"); }
}

export async function shareStory(storyUrl, link, title) {
  try { await navigator.clipboard.writeText(link); } catch {}
  const res = await fetch(storyUrl);
  if (!res.ok) throw new Error("story");
  const blob = await res.blob();
  const file = new File([blob], "historia-pomata.jpg", { type: "image/jpeg" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text: `${title} · ${link}` });
      toast.success("Enlace copiado: en Instagram añade el sticker «Enlace» y pégalo para que se pueda pinchar", { duration: 7000 });
      return;
    } catch (e) { if (e?.name === "AbortError") return; }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = "historia-pomata.jpg"; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  toast.success("Imagen de historia descargada y enlace copiado: súbela a Instagram y añade el sticker «Enlace»", { duration: 7000 });
}

export function ShareButton({ url, storyUrl, title, testid, className = "", size = "w-4 h-4", label }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const doStory = async (e) => {
    stop(e); setBusy(true);
    try { await shareStory(storyUrl, url, title); } catch { toast.error("No se pudo generar la historia"); } finally { setBusy(false); setOpen(false); }
  };
  if (!storyUrl) {
    return (
      <button type="button" onClick={(e) => { stop(e); shareLink(url, title); }} data-testid={testid} title="Compartir"
        className={`rounded-lg text-white/60 hover:text-red-500 hover:bg-red-600/10 shrink-0 flex items-center gap-2 ${className}`}>
        <Share2 className={size} />{label && <span>{label}</span>}
      </button>
    );
  }
  return (
    <span ref={ref} className="relative inline-flex shrink-0" onClick={stop}>
      <button type="button" onClick={(e) => { stop(e); setOpen((o) => !o); }} data-testid={testid} title="Compartir"
        className={`rounded-lg text-white/60 hover:text-red-500 hover:bg-red-600/10 shrink-0 flex items-center gap-2 ${className}`}>
        {busy ? <Loader2 className={`${size} animate-spin`} /> : <Share2 className={size} />}{label && <span>{label}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-xl border border-white/10 bg-[#111116] shadow-2xl p-1.5 text-left" data-testid={`${testid}-menu`}>
          <button type="button" onClick={(e) => { stop(e); setOpen(false); shareLink(url, title); }} data-testid={`${testid}-link`}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-lg text-white/80 hover:bg-white/[0.05] hover:text-white">
            <Link2 className="w-3.5 h-3.5 text-red-400" /> Compartir enlace
          </button>
          <button type="button" onClick={doStory} disabled={busy} data-testid={`${testid}-instagram`}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-lg text-white/80 hover:bg-white/[0.05] hover:text-white disabled:opacity-50">
            <Instagram className="w-3.5 h-3.5 text-pink-400" /> Historia de Instagram
          </button>
        </div>
      )}
    </span>
  );
}
